import React, { useEffect, useMemo, useState } from "react";
import Cookies from "universal-cookie";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import BaseViewModel from "../../base/BaseViewModel";
import actions, { useActions } from "../../redux/actions";
import ErrorHandler from "../../base/ErrorHandler";
import {
  getMultiLangProperty,
  setLocalSearchHistory,
  useSharePreview,
} from "../../helpers/generalHelper";

const cookies = new Cookies();

const DEFAULT_SEARCH_FILTERS = {
  _limit: 15,
  _page: 1,
  isParent: true,
  _sort: "-updatedAt",
};

function useShopGridStandard() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { pathname } = location || {};
  const token = cookies.get("token", { path: "/" }) || "";

  const ProductActions = useActions(actions.ProductActions);
  const CartActions = useActions(actions.CartActions);
  const MerchantActions = useActions(actions.MerchantActions);
  const CategoryActions = useActions(actions.CategoryActions);

  const cartItems = useSelector((state) => state.cartData);
  const currency = useSelector((state) => state.currencyData);
  const compareItems = useSelector((state) => state.compareData);
  const { wishlistItems } = useSelector((state) => state.wishlistData);
  const categories = useSelector((state) => state.category);
  const storeConfig = useSelector((state) => state.storeConfig);

  const [layout, setLayout] = useState("grid three-column");
  const [offset, setOffset] = useState(0);
  const [productList, setProductList] = useState([]);
  const [productListWithStock, setProductListWithStock] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState(
    history.location.state?.search || null
  );
  const [searchFilters, setSearchFilters] = useState(DEFAULT_SEARCH_FILTERS);
  const [totalRecords, setTotalRecords] = useState(0);
  const [merchants, setMerchants] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [refreshSearchHistoryList, setRefreshSearchHistoryList] = useState(
    false
  );
  const [selectedCategory, setSelectedCategory] = useState(null);

  const { metaTitle, metaTags } = useSharePreview(
    getMultiLangProperty(selectedCategory?.name, i18n.language),
    selectedCategory?.og?.description ||
      getMultiLangProperty(selectedCategory?.description, i18n.language),
    Array.isArray(selectedCategory?.media) && selectedCategory?.media.length > 0
      ? selectedCategory?.media[0]?.fileUrl
      : null
  );

  useEffect(() => {
    const names = pathname.split("/");
    const slug = names.pop();
    if (slug === "browse") setSelectedKey(null);
    else setSelectedKey(slug);
  }, [pathname]);

  const { hashtag } = useParams();

  const getLayout = (layoutInput) => {
    setLayout(layoutInput);
  };

  const getFilterSortParams = (value) => {
    setSearchFilters({ ...DEFAULT_SEARCH_FILTERS, _sort: value });
  };

  const setCurrentPage = (value) => {
    setSearchFilters({ ...searchFilters, _page: value });
  };

  const getStock = async (id) =>
    ProductActions.getStock(id)
      .then((response) => {
        const {
          message: {
            record: { isInStock, qty },
          },
        } = response || {};
        return { isInStock, qty };
      })
      .catch((error) => {
        ErrorHandler(error);
        return false;
      });

  const getProductListWithStock = async (products) => {
    const newProductList = await Promise.all(
      products.map(async (product) => {
        const { isInStock, qty } = await getStock(product._id);
        return { ...product, isInStock, qty };
      })
    );
    setProductListWithStock(newProductList);
  };

  const addToCart = (id) => {
    CartActions.addToCart(token, cartItems._id, id, 1, cartItems)
      .then(() => CartActions.getOne(token, cartItems._id))
      .catch((e) => {
        const { code } = e;
        if (code === 42006) {
          window.alert(t("cart.tierRestriction"));
          window.location.reload();
        } else {
          window.alert(t("home.outOfStock"));
        }
      });
  };

  const findCategoryBySlug = (slug) =>
    CategoryActions.getBySlug(token, slug)
      .then((response) => {
        const { message } = response || {};
        const { records } = message || {};
        const [category] = records || [];
        return category;
      })
      .catch(() => null);

  useEffect(() => {
    getProductListWithStock(productList);
  }, [productList]);

  useEffect(() => {
    if (selectedKey) {
      ProductActions.getByCategory(selectedKey, searchFilters)
        .then(async (response) => {
          const {
            message: { records = [], count = 0 },
          } = response;
          const category = await findCategoryBySlug(selectedKey);
          setSelectedCategory(category);
          setProductList(records);
          setTotalRecords(count);
        })
        .catch(() => {
          setProductList([]);
          setTotalRecords(0);
          setSelectedCategory(null);
        });
    } else {
      ProductActions.getList(null, searchFilters)
        .then((response) => {
          const {
            message: { records = [], count = 0 },
          } = response;
          setProductList(records);
          setTotalRecords(count);
          setSelectedCategory(null);
        })
        .catch(() => {
          setProductList([]);
          setTotalRecords(0);
          setSelectedCategory(null);
        });
    }
  }, [selectedKey, searchFilters, i18n.language, ProductActions]);

  useEffect(() => {
    const newSearchFilters = {
      ...searchFilters,
      _sort: "-updatedAt",
      _page: 1,
    };
    if (history.location.state?.brandid) {
      newSearchFilters.merchant = history.location.state?.brandid;
    } else {
      delete newSearchFilters.merchant;
    }

    if (history.location.state?.search)
      newSearchFilters.name = history.location.state?.search;
    setSearchFilters(newSearchFilters);
  }, [history.location.state, history]);

  const cleanBrandsFilter = async () => {
    if (
      history.location &&
      history.location.state &&
      history.location.state.brandid
    ) {
      const state = { ...history.location.state };
      delete state.brandid;
      await history.replace({ ...history.location, state });
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    const value = e.target.keyword.value || "";
    if (value !== "") {
      setSearchFilters({
        ...searchFilters,
        _sort: "-updatedAt",
        _page: 1,
        name: value,
      });
      setLocalSearchHistory(value);
      setRefreshSearchHistoryList(true);
    } else {
      setSearchFilters(DEFAULT_SEARCH_FILTERS);
    }
  };

  useEffect(() => {
    MerchantActions.getList(null, { type: "user_defined", status: "enabled" })
      .then((response) => {
        const {
          message: { records = [] },
        } = response || {};
        setMerchants(records);
      })
      .catch(() => {
        setMerchants([]);
      });
  }, []);

  const onSubmit = (mobile) => {
    if (!mobile) {
      window.alert(t("customerRedemption.enterMobile"));
      return;
    }

    window.alert("Coming soon...");

    //  TODO: validate user via mobile
  };

  const onClickDeleteRecord = (record) => {
    const wholeSearchItemArray = [];

    const searchHistoryRecord = JSON.parse(
      localStorage.getItem("searchKeywordHistory")
    );

    searchHistoryRecord &&
      searchHistoryRecord.forEach((record) => {
        wholeSearchItemArray.push(record);
      });

    const index = wholeSearchItemArray.indexOf(record);
    if (index > -1) {
      wholeSearchItemArray.splice(index, 1);
    }

    localStorage.setItem(
      "searchKeywordHistory",
      JSON.stringify(wholeSearchItemArray)
    );

    setRefreshSearchHistoryList(true);
  };

  return {
    layout,
    productListWithStock,
    searchKeyword,
    hashtag,
    pathname,
    getLayout,
    getFilterSortParams,
    onSearch,
    searchFilters,
    setCurrentPage,
    setOffset,
    totalRecords,
    addToCart,
    cartItems,
    currency,
    compareItems,
    wishlistItems,
    merchants,
    selectedKey,
    setSelectedKey,
    cleanBrandsFilter,
    onSubmit,
    history,
    refreshSearchHistoryList,
    setRefreshSearchHistoryList,
    setSearchFilters,
    onClickDeleteRecord,
    categories,
    selectedCategory,
    metaTitle,
    metaTags,
  };
}

const ShopGridStandardViewModel = BaseViewModel(useShopGridStandard);

export default ShopGridStandardViewModel;
